import { Stack, Typography, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useContext, useEffect, useState } from 'react';
import Iconify from 'src/components/iconify';
import { getPreScreeningQuestions } from 'src/services/candidate.service';
import PropTypes from 'prop-types';
import { useResponsive } from 'src/hooks/use-responsive';
import FallbackUI from './fallback-ui';
import ProfileLoader from './loader';
import { CandidateProfileContext } from './context/candidate-profile-context';
import { fThousandSeparator } from 'src/utils/format-number';

const useStyles = makeStyles(theme => ({
  screeningItemDiv: {
    border: `1px solid ${theme.palette.border.primaryGreen1}`,
  },
  screeningItemDivBasic: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
  },
  screeningItemDivError: {
    border: `1px solid ${theme.palette.border.primaryRed1}`,
  },
  qualifiedIndicatorDiv: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    gap: 6,
    padding: '6px 8px',
    width: 90,
    borderRadius: 22,
    background: theme.palette.content.primaryGreen2,
    color: theme.palette.background.primaryGreen1,
  },
  tickIcon: {
    color: theme.palette.background.primaryGreen1,
  },
  crossIcon: {
    color: theme.palette.background.primaryRed1,
  },
  disqualifiedIndicatorDiv: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    gap: 6,
    padding: '6px 8px',
    width: 105,
    borderRadius: 22,
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
  },
  qualifyTag: {
    marginLeft: '8px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 15,
    display: 'inline-flex',
    background: theme.palette.background.primaryAccent2,
  },
}));

const QualifiedTag = ({ success }) => {
  // hooks
  const classes = useStyles();

  return (
    <Stack
      className={
        success
          ? classes.qualifiedIndicatorDiv
          : classes.disqualifiedIndicatorDiv
      }
      sx={{
        right: '10px',
        top: { xs: '12px', md: '16px' },
      }}
    >
      {success ? (
        <Iconify
          width={16}
          height={16}
          className={classes.tickIcon}
          icon="tabler:circle-check-filled"
        />
      ) : (
        <Iconify
          width={16}
          height={16}
          className={classes.crossIcon}
          icon="tabler:circle-x-filled"
        />
      )}
      <Typography variant="caption" sx={{ fontSize: '12px' }}>
        {success ? 'Qualified' : 'Disqualified'}
      </Typography>
    </Stack>
  );
};

const BOOLEAN_RESPONSES = {
  YES: 'Yes',
  TRUE: 'Yes',
  NO: 'No',
  FALSE: 'No',
};

const PreScreeningGroup = ({ jobId }) => {
  // hooks
  const classes = useStyles();
  const { obj: candidateDetails, token } = useContext(CandidateProfileContext);
  const mdUp = useResponsive('up', 'md');

  // states
  const [preScreeningData, setPreScreeningData] = useState(null);
  const [isEvaluated, setIsEvaluated] = useState(false);
  const [qualified, setQualified] = useState(null);
  const [hasSolutions, setHasSolutions] = useState(false);
  const [isPreScreeningLoading, setIsPreScreeningLoading] = useState(true);

  // effects
  useEffect(() => {
    setIsPreScreeningLoading(true);

    getPreScreeningQuestions(
      jobId,
      candidateDetails.candidate_info.user_id,
      token,
    )
      .then(res => {
        if (res.success) {
          const _prescreeningQuestions = res?.body?.questions?.map(item => {
            const question = {
              questionId: item?.question_id,
              responseParameters: item?.response_parameters,
              question: item?.text,
              isQualifyingQuestion: item?.is_qualifying_question,
              responseType: item?.response_type,
              response: null,
              title: item?.title,
            };

            return question;
          });

          res?.body?.solutions?.forEach((solution, index) => {
            const questionObj = _prescreeningQuestions.find(
              question => question.questionId === solution.question_id,
            );

            if (questionObj) {
              questionObj.response = {
                responseText: solution?.response_text,
                isEvaluated: solution?.is_evaluated,
                success: solution?.score === 1,
              };
            }
          });

          setHasSolutions(res?.body?.solutions?.length > 0);
          setQualified(res?.body?.is_qualified);
          setIsEvaluated(res?.body?.is_evaluated);
          setPreScreeningData(_prescreeningQuestions);
        } else {
          setHasSolutions(false);
          setQualified(null);
          setPreScreeningData([]);
          throw new Error(res.message);
        }
      })
      .catch(err => console.log(err))
      .finally(() => {
        setIsPreScreeningLoading(false);
      });
  }, [candidateDetails.candidate_info.user_id, jobId]);

  return isPreScreeningLoading ? (
    <ProfileLoader />
  ) : (
    <Stack spacing="24px" position="relative">
      {qualified !== null && (
        <Stack
          padding="11.5px 16px"
          bgcolor={
            qualified ? 'background.primaryGreen2' : 'background.primaryRed2'
          }
          color={qualified ? 'content.primaryGreen1' : 'content.primaryRed1'}
        >
          <Typography
            variant="subheading1"
            fontWeight={{
              xs: '16px',
              md: '20px',
            }}
          >
            {qualified ? 'Qualified' : 'Disqualified'}
          </Typography>
        </Stack>
      )}

      {(!preScreeningData ||
        preScreeningData?.length === 0 ||
        !hasSolutions ||
        isEvaluated === null) && <FallbackUI content="No data found" />}

      {isEvaluated !== null &&
        preScreeningData?.map((item, index) => {
          if (item?.response === null) return null;

          let screeningItemDivClass = classes.screeningItemDivBasic;

          if (item?.isQualifyingQuestion && item?.response?.isEvaluated) {
            screeningItemDivClass = classes.screeningItemDiv;
            if (!item?.response?.success) {
              screeningItemDivClass = classes.screeningItemDivError;
            }
          }

          return (
            <Stack
              key={index}
              className={screeningItemDivClass}
              sx={{
                padding: {
                  xs: '16px',
                  md: '20px',
                },
                borderRadius: '6px',
                position: 'relative',
              }}
              spacing={{
                xs: '28px',
                md: '24px',
              }}
            >
              {item?.isQualifyingQuestion &&
                item?.response &&
                item?.response?.isEvaluated && (
                  <QualifiedTag success={item?.response?.success} />
                )}

              <Stack
                spacing={{
                  xs: '20px',
                  md: '20px',
                }}
              >
                <Typography
                  variant={mdUp ? 'h4' : 'subheading1'}
                  fontSize={{
                    xs: '16px',
                    md: '20px',
                  }}
                  sx={{
                    margin: 0,
                    width: {
                      xs: '75%',
                      md: '90%',
                    },
                  }}
                >
                  {item?.title}
                  {item.isQualifyingQuestion && (
                    <Box
                      padding={{
                        xs: '3px 16px',
                        md: '4px 13px',
                      }}
                      className={classes.qualifyTag}
                    >
                      <Typography variant="caption" sx={{ fontSize: '10px' }}>
                        Must Qualify
                      </Typography>
                    </Box>
                  )}
                </Typography>

                <Typography variant="input" fontWeight={500}>
                  {item?.question}
                </Typography>
              </Stack>

              <Stack
                spacing="12px"
                justifyContent="space-between"
                direction="row"
              >
                <Stack spacing="10px" flexGrow={1}>
                  <Typography variant="subheading1" fontSize="16px">
                    Candidate response
                  </Typography>
                  <Typography variant="input" fontWeight={500}>
                    {item?.responseType === 'NUMERIC'
                      ? fThousandSeparator(item?.response?.responseText)
                      : item?.responseType === 'BOOLEAN'
                        ? BOOLEAN_RESPONSES[item?.response?.responseText]
                        : item?.response?.responseText}
                  </Typography>
                </Stack>
                {item?.responseType !== 'TEXT' && (
                  <Stack
                    sx={{
                      color: theme => theme.palette.content.primaryBlack3,
                      gap: '10px',
                      flexGrow: 1,
                    }}
                  >
                    <Typography variant="subheading1" fontSize="16px">
                      Ideal response
                    </Typography>
                    <Typography variant="input" fontWeight={500}>
                      {item?.responseType === 'NUMERIC' &&
                        `${fThousandSeparator(
                          item?.responseParameters?.MIN_VALUE,
                        )}-${fThousandSeparator(
                          item?.responseParameters?.MAX_VALUE,
                        )}`}
                      {item?.responseType === 'BOOLEAN' &&
                        (BOOLEAN_RESPONSES[
                          item?.responseParameters?.IDEAL_ANSWER
                        ] ||
                          item?.responseParameters?.IDEAL_ANSWER)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          );
        })}
    </Stack>
  );
};

export default PreScreeningGroup;

PreScreeningGroup.propTypes = {
  jobId: PropTypes.string.isRequired,
};

QualifiedTag.propTypes = {
  success: PropTypes.bool.isRequired,
};
